// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
import React /* eslint-disable-line no-unused-vars */, {
  type Node,
} from 'react';

import { UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH } from '@zapier/design-system-patterns/src/components/UniversalNav/UniversalSidebar';
import { Colors, Link } from '@zapier/design-system';
import { SkipLink, ZapierLogo } from '@zapier/design-system-patterns';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

import AccountActions from './AccountActions';
import UniversalTopbarLink from './UniversalTopbarLink';
import { UniversalTopbarLinks, skipLinkHref } from '../constants';

import type { UniversalNavLinkType } from '@zapier/design-system-patterns';

/** @jsx isolateComponent */

export const UNIVERSAL_TOPBAR_CONDENSED_HEIGHT = 50;
export const UNIVERSAL_TOPBAR_HEIGHT = 70;

export type Props = {|
  /*
   * Indicates whether a slot exists to render the collapse button.
   * Doesn't render the collapse button itself though, since in some
   * instances `UniversalSidebar` may overlap the empty slot.
   */
  hasCollapseButtonSlot: boolean,
  /**
   * An array of links to be rendered inside the main navigation.
   */
  links: Array<UniversalNavLinkType>,
  /**
   * Zapier logo label for link
   */
  logoAriaLabel: string,
  /**
   * Zapier logo link
   */
  logoHref: string,
  /**
   * Optional handler for when anywhere on the top nav is clicked.
   */
  onClick?: (SyntheticMouseEvent<HTMLElement>) => void,
  /**
   * Zapier logo link callback
   */
  onLogoClick?: () => void,
  /**
   * Renders an account menu if provided. This overrides the default account actions menu (Sign In, Sign Up)
   */
  renderAccountMenu: (props: Props) => Node,
  /**
   * Renders notifications component if provided
   */
  renderNotifications: (props: Props) => Node,
  /**
   * Render the sidebar component if provided
   */
  renderSidebar?: (props: Props) => Node,
  /**
   * Currently selected link inside links
   */
  selectedHref: string,
  /**
   * If true, renders submenu if present in a link
   */
  shouldRenderSubmenu: boolean,
  /**
   * Size of the topbar which adjusts its height, padding, etc.
   */
  size: 'small' | 'medium',
  /**
   * Anchor to the main content area of the page.
   * **The page must contain a node with the `id` in this prop!**
   */
  skipLinkHref: string,
|};

const Styles = labelStyles(__filename, {
  root: props => [
    {
      alignItems: 'center',
      display: 'grid',
      gridTemplateColumns: 'min-content auto max-content',
      position: 'relative', // Needed for submenu dropdown
      width: '100%',
      backgroundColor: Colors.white,
      borderBottom: `1px solid ${Colors.grey200}`,
    },
    withModifier(
      props.size,
      {
        small: {
          gap: 5,
          height: UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
          padding: 5,
        },
        medium: {
          gap: 10,
          height: UNIVERSAL_TOPBAR_HEIGHT,
          padding: 10,
        },
      }[props.size]
    ),
  ],
  group: {
    display: 'grid',
    gridAutoFlow: 'column',
    alignItems: 'center',
    gap: 'inherit',
  },
  sidebarSlot: (props: Props) => [
    withModifier(
      props.size,
      {
        small: {
          minWidth: 'auto',
        },
        medium: {
          minWidth: UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH,
        },
      }[props.size]
    ),
  ],
  mainNavigationContent: (props: Props) => [
    {
      justifySelf: 'center',
      display: 'grid',
      gridTemplateColumns: 'auto 1fr',
      alignItems: 'center',
      gap: 40,
    },
    props.links.length === 0 &&
      withModifier('linkless', {
        justifySelf: 'end',
        gridTemplateColumns: 'auto',
      }),
  ],
  navLinks: {
    // Since we do not have a limit on the number of links,
    // this neatly wraps the nav section to prevent column overlap
    overflow: 'auto',
    lineHeight: 1.5,
  },
  navLinksList: (props: Props) => ({
    display: 'grid',
    gridGap: 40,
    gridTemplateColumns: `repeat(${props.links.length}, max-content)`,
  }),
  logoLinksWrapper: {
    // This section has an element with position: absolute,
    // relative positioning here will define the bounds
    position: 'relative',
  },
  skipLink: {
    position: 'absolute',
    // Positions the SkipLink just below the logo
    top: '100%',
    whiteSpace: 'nowrap',
  },
});

const Links = (props: Props) => {
  const links = props.links.map(link => (
    <li key={link.href}>
      <UniversalTopbarLink
        href={link.href}
        isSelected={props.selectedHref === link.href}
        shouldRenderSubmenu={props.shouldRenderSubmenu}
        submenu={link.submenu}
      >
        {link.text}
      </UniversalTopbarLink>
    </li>
  ));
  return (
    <nav css={Styles.navLinks}>
      <ul css={Styles.navLinksList(props)}>{links}</ul>
    </nav>
  );
};

/**
 * Renders universal navigation top bar with Zapier logo, search,
 * links and account actions.
 */
const UniversalTopbar = (props: Props) => {
  const sidebar = props.renderSidebar && props.renderSidebar(props);
  const logoSize = {
    small: { width: 68, height: 31 },
    medium: { width: 80, height: 37 },
  }[props.size];
  return (
    // onClick here exists to support behaviour logic might needed by other components of the Nav.
    //  E.g; Closing the sidebar opened by Topbar if another element on Topbar is clicked.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <header
      css={Styles.root(props)}
      data-testid="universalTopbar"
      onClick={props.onClick}
    >
      <div css={Styles.group}>
        {sidebar && <div css={Styles.sidebarSlot(props)}>{sidebar}</div>}
        <div css={Styles.logoLinksWrapper}>
          <Link
            ariaLabel={props.logoAriaLabel}
            href={props.logoHref}
            onClick={props.onLogoClick}
          >
            <ZapierLogo {...logoSize} />
          </Link>
          <div css={Styles.skipLink}>
            <SkipLink href={props.skipLinkHref} />
          </div>
        </div>
      </div>
      <div css={Styles.mainNavigationContent(props)}>
        {props.links.length > 0 && <Links {...props} />}
      </div>
      <div css={Styles.group}>
        {props.renderNotifications(props)}
        {props.renderAccountMenu(props)}
      </div>
    </header>
  );
};

UniversalTopbar.defaultProps = {
  hasCollapseButtonSlot: false,
  links: UniversalTopbarLinks,
  logoAriaLabel: 'Go to the Zapier Homepage',
  logoHref: '/',
  onLogoClick: () => {},
  renderAccountMenu: (props: Props) => (
    <AccountActions
      selectedHref={props.selectedHref}
      shouldRenderSignIn={props.size !== 'small'}
      shouldRenderSignUp={true}
      size={props.size}
    />
  ),
  renderNotifications: () => null,
  selectedHref: '',
  shouldRenderSubmenu: false,
  size: 'medium',
  skipLinkHref,
};

export default UniversalTopbar;
